import { useEffect, useMemo, useRef } from 'react';
import { PrefillType } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { CareCreditIntegrationCustom, CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';
import { InvoiceModel } from '@frontend/api-invoices';
import { PersonsV3 } from '@frontend/api-person';
import { CareCreditChipStatus, CareCreditPatientData } from '../types/care-credit.types';
import { getQuickOfferStatus } from '../widgets/care-credit-enrollment-multi-step/utils/quick-offer';
import { useCareCreditPurlQuery } from './use-care-credit-purl-query';

export type UseCareCreditPatientDataProps = {
  person: Person | undefined;
  locationId: string | undefined;
  merchantNumber: string | undefined;
  isDecoupled: boolean;
  userId: string;
  invoice?: InvoiceModel;
};
export type UseCareCreditPatientDataOptions = {
  enabled?: boolean;
  usePurlFallback?: boolean;
  prefillType?: PrefillType;
};

export const useCareCreditPatientData = (
  { person, locationId, merchantNumber, isDecoupled, userId, invoice }: UseCareCreditPatientDataProps,
  { enabled, usePurlFallback = true, prefillType }: UseCareCreditPatientDataOptions = {}
) => {
  const queryClient = useQueryClient();
  const personDataSignature = JSON.stringify(CareCreditIntegrationCustom.getPatientQueryFields(person));
  const previousPersonDataSignature = useRef<string>();

  useEffect(() => {
    if (
      previousPersonDataSignature.current &&
      previousPersonDataSignature.current !== personDataSignature &&
      person?.personId &&
      locationId &&
      merchantNumber
    ) {
      queryClient.invalidateQueries({
        queryKey: CareCreditIntegrationCustom.getCareCreditPatientDataQueryKey(person, locationId, merchantNumber),
        exact: true,
      });
    }

    previousPersonDataSignature.current = personDataSignature;
  }, [locationId, merchantNumber, person, personDataSignature, queryClient]);

  const {
    query: { isLoading, isRefetching, isFetching, isSuccess, error, data },
  } = CareCreditIntegrationCustom.useCareCreditPatientDataQuery(
    {
      person,
      merchantNumber,
      locationId,
    },
    {
      enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const quickOfferStatus = data ? getQuickOfferStatus(data) : undefined;

  // Use the same mapping as the contact panel: MobilePhone with fallback to OtherContactInfo (UNSPECIFIED)
  const convertedPerson = useMemo(() => PersonsV3.PersonHelpers.convertPersonV3ToPerson(person), [person]);
  const mobilePhone = convertedPerson.MobilePhone || convertedPerson.OtherContactInfo;

  const {
    data: accountLookupData,
    isLoading: isAccountLookupLoading,
    isRefetching: isAccountLookupRefetching,
    isFetching: isAccountLookupFetching,
  } = CareCreditIntegrationV1.Queries.useAccountLookupQuery({
    request: { merchantNumber, phoneNumber: mobilePhone, locationId },
    options: {
      enabled:
        enabled !== false && !!merchantNumber && !!mobilePhone && !!locationId && quickOfferStatus === 'CARDHOLDER',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });
  const isAccountNumberLoading = isAccountLookupLoading || isAccountLookupRefetching || isAccountLookupFetching;

  const { purl, isPurlLoading, purlError } = useCareCreditPurlQuery(
    {
      locationId,
      isDecoupled,
      userId,
      careCreditPurlTransaction: {
        // invoice number is different from the paymentRequestId
        // in that it is entered by the user in the case of a
        // decoupled flow and can be used by the user when
        // reconciling their PMS ledger against care credit
        invoiceNumber: invoice?.id || '',
        // the paymentRequestId is the weave identifier
        // for the payment request.
        paymentRequestId: invoice?.id || '',
        purchaseAmountInCents: invoice?.billedAmount || 0,
        serviceDate: invoice?.billedAt || '',
      },
      prefillType,
      person,
    },
    {
      enabled:
        enabled !== false &&
        usePurlFallback &&
        !error &&
        !!quickOfferStatus &&
        quickOfferStatus !== 'UNKNOWN' &&
        (prefillType !== PrefillType.PREFILL_TYPE_UNSPECIFIED ||
          (!isLoading && !isRefetching && !isFetching && !data?.locationUrl)),
    }
  );

  const careCreditData = useMemo<CareCreditPatientData | undefined>(() => {
    if (!data) {
      return undefined;
    }

    const status = getQuickOfferStatus(data);
    const accountNumber = accountLookupData?.data?.account?.accountInfo?.accountNumber;

    if (!status) {
      return undefined;
    }

    if (status === 'CARDHOLDER') {
      const accountLookupBalance = accountLookupData?.data?.account?.creditInfo?.availableCredit;
      const quickOfferBalance = data.errorResponse?.accountInfo?.availableBalance;
      const availableCredit =
        accountLookupBalance !== undefined && accountLookupBalance !== null && accountLookupBalance !== ''
          ? accountLookupBalance
          : quickOfferBalance;

      return {
        personId: person?.personId || '',
        status,
        amount: Number(availableCredit) || 0,
        careCreditPersonId: data.errorResponse?.accountInfo?.accountNumber || accountNumber || '',
        accountNumber,
        asOfDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        patientLink: purl?.purl || data.locationUrl || '',
      };
    }
    return {
      personId: person?.personId || '',
      status,
      amount: data.plccOfferInfo?.creditLine ? Number(data.plccOfferInfo.creditLine) : 0,
      careCreditPersonId: '',
      accountNumber,
      asOfDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      patientLink: purl?.purl || data.locationUrl || '',
    };
  }, [
    accountLookupData?.data?.account?.accountInfo?.accountNumber,
    accountLookupData?.data?.account?.creditInfo?.availableCredit,
    person?.personId,
    purl?.purl,
    data,
  ]);

  const careCreditChipStatus: CareCreditChipStatus | undefined =
    isLoading || isRefetching || isFetching
      ? undefined
      : error || (data && !quickOfferStatus)
      ? 'ERROR'
      : quickOfferStatus || 'UNKNOWN';

  return {
    isBusy: isLoading || isRefetching || isFetching || isPurlLoading || isAccountNumberLoading,
    isLoading: isLoading || isPurlLoading || isAccountNumberLoading,
    isSuccess,
    error,
    purlError,
    isAccountNumberLoading,
    errorResponse: data?.errorResponse,
    careCreditData,
    careCreditChipStatus,
  };
};

export type UseCareCreditPatientData = ReturnType<typeof useCareCreditPatientData>;
